import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "@/api/client";
import { Send, MessagesSquare, Check, CheckCheck, Sparkles, Pin } from "lucide-react";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/contexts/AuthContext";

const AI_USER_ID = "ai-assistant-system-user";
const AI_USER = { id: AI_USER_ID, name: "AI Asistan", avatar_color: "#7C3AED", title: "Sürekli çalışan asistan", role: "ai_assistant" };

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso); const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff/60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff/3600)} sa önce`;
  return d.toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
}

export default function MessagesPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [threads, setThreads] = useState([]); const [team, setTeam] = useState([]);
  const [active, setActive] = useState(params.get("u") || AI_USER_ID);
  const [msgs, setMsgs] = useState([]); const [text, setText] = useState("");
  const [presence, setPresence] = useState({});
  const [aiTyping, setAiTyping] = useState(false);
  const scrollRef = useRef(null);

  const loadThreads = () => apiClient.get("/messages/threads").then(r => setThreads(r.data));
  const loadMsgs = (other) => apiClient.get(`/messages/${other}`).then(r => setMsgs(r.data));
  const loadPresence = () => apiClient.get("/team/presence").then(r => setPresence(r.data));

  useEffect(() => { loadThreads(); loadPresence(); apiClient.get("/team").then(r => setTeam([AI_USER, ...r.data.filter(u => u.id !== user?.id && u.id !== AI_USER_ID)])); }, [user]);
  useEffect(() => { if (active) loadMsgs(active); }, [active]);
  useEffect(() => { const t = setInterval(() => { if (active && active !== AI_USER_ID) loadMsgs(active); if (active === AI_USER_ID && !aiTyping) loadMsgs(active); loadThreads(); loadPresence(); }, 6000); return () => clearInterval(t); }, [active, aiTyping]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const content = text; setText("");
    if (active === AI_USER_ID) {
      // optimistic add user message
      setMsgs(m => [...m, { id: "tmp-"+Date.now(), from_user_id: user.id, to_user_id: AI_USER_ID, body: content, created_at: new Date().toISOString(), is_read: true }]);
      setAiTyping(true);
      try {
        await apiClient.post("/ai-assistant/chat", { content });
        loadMsgs(active);
      } finally { setAiTyping(false); loadThreads(); }
    } else {
      await apiClient.post("/messages", { to_user_id: active, body: content });
      loadMsgs(active); loadThreads();
    }
  };

  const otherUser = team.find(t => t.id === active);
  const otherPres = active && presence[active];
  const isAI = active === AI_USER_ID;

  return (
    <div className="space-y-3" data-testid="messages-page">
      <div className="flex items-center justify-between"><div><h1 className="text-2xl font-bold">Mesajlar</h1><p className="text-sm text-[#64748B] mt-1">Ekip içi sohbet + AI Asistan</p></div></div>
      <div className="ky-card grid grid-cols-12 overflow-hidden" style={{ height: "calc(100vh - 200px)", minHeight: "520px", maxHeight: "820px" }}>
        <div className="col-span-4 border-r border-[#ECECEF] flex flex-col">
          <div className="px-3 py-2 border-b border-[#ECECEF] text-xs font-semibold text-[#64748B]">Konuşmalar</div>
          <div className="flex-1 overflow-y-auto">
            {team.map(u => {
              const isAiItem = u.id === AI_USER_ID;
              const th = threads.find(t => t.user?.id === u.id);
              const p = presence[u.id];
              return (
                <button key={u.id} onClick={() => setActive(u.id)} className={`w-full flex items-center gap-2 px-3 py-2.5 border-b border-[#ECECEF] hover:bg-[#FAFAFB] text-left ${active === u.id ? "bg-[#EEF2FF]" : ""} ${isAiItem ? "bg-gradient-to-r from-[#F5F3FF] to-transparent" : ""}`} data-testid={`thread-${u.id}`}>
                  <div className="relative">
                    {isAiItem ? <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] flex items-center justify-center text-white"><Sparkles size={14}/></div> : <Avatar name={u.name} color={u.avatar_color} size={32}/>}
                    {!isAiItem && p?.online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-white"/>}
                    {isAiItem && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-white"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-medium truncate flex items-center gap-1">{u.name}{isAiItem && <Pin size={10} className="text-[#7C3AED]"/>}</div>
                      {th?.last && <div className="text-[10px] text-[#94A3B8]">{relTime(th.last.created_at)}</div>}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-[11px] text-[#94A3B8] truncate flex-1">{th?.last?.body || (isAiItem ? "Sorularını sor, süreçlerini özetleyeyim" : (p?.online ? "Çevrimiçi" : p?.last_seen ? `Son görülme ${relTime(p.last_seen)}` : "Mesaj başlat"))}</div>
                      {th?.unread > 0 && <span className="ml-1 min-w-[18px] h-[18px] text-[10px] font-bold bg-[#4F46E5] text-white rounded-full flex items-center justify-center px-1">{th.unread}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="col-span-8 flex flex-col min-h-0">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#94A3B8] flex-col gap-2"><MessagesSquare size={32}/>Konuşma seçin</div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#ECECEF] flex items-center gap-2">
                <div className="relative">
                  {isAI ? <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] flex items-center justify-center text-white"><Sparkles size={14}/></div> : <Avatar name={otherUser?.name} color={otherUser?.avatar_color} size={32}/>}
                  {(isAI || otherPres?.online) && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#10B981] border-2 border-white"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{isAI ? "AI Asistan" : otherUser?.name}</div>
                  <div className="text-[11px] text-[#64748B]">{isAI ? <span className="text-[#7C3AED] font-medium">Gemini 2.5 Flash · 7/24 çalışıyor</span> : otherPres?.online ? <span className="text-[#10B981] font-medium">● Çevrimiçi</span> : otherPres?.last_seen ? `Son görülme: ${relTime(otherPres.last_seen)}` : (otherUser?.title || "—")}</div>
                </div>
                {isAI && user?.role === "admin" && <button onClick={async ()=>{ await apiClient.post("/ai-assistant/proactive-brief"); loadMsgs(active); }} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="ai-proactive-brief"><Sparkles size={11}/>Günlük Brief Üret</button>}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
                {msgs.map(m => {
                  const mine = m.from_user_id === user?.id;
                  const fromAI = m.from_user_id === AI_USER_ID;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-[#4F46E5] text-white" : fromAI ? "bg-gradient-to-br from-[#F5F3FF] to-[#EEF2FF] border border-[#C7D2FE]" : "bg-[#F4F4F5]"}`}>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                        <div className={`flex items-center gap-1 text-[10px] mt-1 ${mine ? "text-white/70 justify-end" : "text-[#94A3B8]"}`}>
                          <span>{new Date(m.created_at).toLocaleTimeString("tr-TR",{hour:"2-digit", minute:"2-digit"})}</span>
                          {mine && (m.is_read ? <CheckCheck size={11}/> : <Check size={11}/>)}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {aiTyping && <div className="flex justify-start"><div className="px-3 py-2 rounded-2xl bg-gradient-to-br from-[#F5F3FF] to-[#EEF2FF] border border-[#C7D2FE] text-sm flex items-center gap-1"><Sparkles size={11} className="text-[#7C3AED] animate-pulse"/><span className="text-[#7C3AED]">Düşünüyor…</span></div></div>}
                {msgs.length === 0 && !aiTyping && <div className="text-center text-xs text-[#94A3B8] mt-10">{isAI ? "AI Asistan'a 'Bugün ne yapmalıyım?', 'Hangi domainler yenilenmeli?' gibi sorular sorun." : "Mesaj başlatın 👋"}</div>}
              </div>
              <div className="border-t border-[#ECECEF] p-3 flex gap-2 flex-shrink-0">
                <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder={isAI ? "AI Asistan'a sor…" : "Mesaj…"} className="ky-input flex-1" data-testid="message-input"/>
                <button onClick={send} className="ky-btn ky-btn-accent" data-testid="message-send"><Send size={13}/></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
